// src/contacts/index.js — екран „Контакти" (Фаза 3.2). Пренесен дословно от app.js.
// ВАЖНО: кешът на контактите живее в app.js (window.CRM) — фактурите/проформите
// (легаси) го четат; модулът го пълни през setContacts. Етикетите на формата са
// ДОМ контракт с registry-autofill.js — НЕ се променят.

import { el, clear, $, field, input, row, clickableRow } from '../shared/dom.js';
import { isoToBg, localTodayIso, dateInput, dateInputsValid } from '../shared/dates.js';
import { money, dfmt } from '../shared/format.js';
import { toast } from '../shared/toast.js';
import { api } from '../shared/api.js';
import { emptyState, searchInput } from '../shared/ui.js';
import { regHead, regFilters, regField, regCount, cutCell, REG_TABLE } from '../shared/register.js';
import { openModal, closeModal, confirmDialog } from '../shared/modal.js';
import { viesButton } from '../shared/vies.js';
import { xlsxDownload } from '../shared/xlsx.js';
import { bulkManager } from '../shared/pickers.js';
import { state, cq, activeCompanyObj, contacts as contactsCache, setContacts, isAdminUser } from '../shared/state.js';
import { impReadFile } from '../shared/importers.js';
import { contactsImportPreview } from './import.js';
// Държавите идват през CJS обвивката, не от JSON-а направо — Node ESM
// (jsdom тестовете) не внася JSON без import-атрибут (виж services/countries.js).
import countriesRef from '../../services/countries.js';
// Ключът на номера — същото нормализиране като на сървъра (services/vatKey.js).
import vatKeyRef from '../../services/vatKey.js';

const COUNTRIES = countriesRef.countries || [];
const countryName = (code) => { const x = COUNTRIES.find((k) => k.code === code); return x ? x.name : code; };
// VIES връща Гърция като „EL" (кодът по ДДС), а списъкът е по ISO — „GR".
const VIES_TO_ISO = { EL: 'GR' };
const vatKeyOf = (c) => vatKeyRef.contactKey(c);

// Отметки за проверка (VIES / извличане на данни) — за списъка с клиенти.
function verifyBadges(c) {
  const wrap = el('div', { style: 'display:flex;gap:4px;flex-wrap:wrap' });
  if (c && c.vies_verified) wrap.appendChild(el('span', { class: 'tag green', text: '✓ ДДС проверен', title: 'ДДС номерът е проверен в европейския регистър VIES' + (String(c.vies_verified).length === 10 ? ' на ' + isoToBg(c.vies_verified) : '') }));
  // Масовата проверка (ПРОМТ-36): значката важи само за номера, който е
  // проверен — поправеният ДДС № я сваля сам, без нова проверка.
  if (c && c.vat_check && c.vat_check.status === 'invalid' && c.vat_check.key === (vatKeyOf(c) || {}).key) {
    wrap.appendChild(el('span', { class: 'tag red', text: 'ДДС № невалиден към ' + isoToBg(c.vat_check.date), title: 'Масовата проверка във ВИЕС на ' + isoToBg(c.vat_check.date) + ' каза, че ДДС номерът ' + c.vat_check.key + ' не е валиден. Проверете номера при клиента.' }));
  }
  // Махнатият от проверката невалиден ДДС № (Макс, 30.10.2026: „да оправи
  // грешките") — да се вижда какъв е бил, докато някой не сложи нов.
  if (c && c.vat_removed && !String(c.vat_number || '').trim()) {
    wrap.appendChild(el('span', { class: 'tag red', text: 'ДДС № махнат — невалиден към ' + isoToBg(c.vat_removed.date), title: 'Проверката във ВИЕС на ' + isoToBg(c.vat_removed.date) + ' каза, че ДДС номерът ' + c.vat_removed.number + ' не е валиден, и той беше махнат от контакта. Върнете го от „Мои фирми → Проверка на контрагентите → Поправени автоматично → Върни".' }));
  }
  if (c && c.data_verified) wrap.appendChild(el('span', { class: 'tag blue', text: '✓ От регистъра', title: 'Данните са изтеглени от Търговския регистър' + (String(c.data_verified).length === 10 ? ' на ' + isoToBg(c.data_verified) : '') }));
  if (!c || (!c.vies_verified && !c.data_verified && !wrap.childNodes.length)) wrap.appendChild(el('span', { style: 'color:var(--muted)', text: '—' }));
  return wrap;
}

// Шифрите в списъка: като клиент и (ако е различен) като доставчик — „к10 · д: д05".
function codesText(c) {
  const acc = String(c.acc_code || '').trim();
  const sup = String(c.supplier_code || '').trim();
  const parts = [];
  if (acc) parts.push(acc);
  if (sup && sup.toLowerCase() !== acc.toLowerCase()) parts.push('дост. ' + sup);
  return parts.length ? parts.join(' · ') : '—';
}

export async function view(page) {
  clear(page);
  if (!state().activeCompany) return window.CRM.needsCompany(page);
  const panel = el('div', { class: 'panel' });
  const bulk = bulkManager({ endpoint: '/contacts', refresh: () => window.CRM.renderView($('#page')), label: 'контакта' });
  const tbodyWrap = el('div');
  let filter = '';
  let typeFilter = state()._contactsTab || 'all';
  const isSupplier = (c) => c.type === 'supplier';
  const matchType = (c) => typeFilter === 'all' ? true : (typeFilter === 'suppliers' ? isSupplier(c) : !isSupplier(c));
  // Едно правило за показаното — ползват го таблицата, броячът и експортът
  // (Г.8: сваля се ПОКАЗАНОТО — таб + търсене, не всичко).
  const shownRows = () => contactsCache().filter((c) => matchType(c) && (c.name + c.eik + c.city + c.phone + c.email).toLowerCase().includes(filter));
  const count = regCount();
  function draw() {
    clear(tbodyWrap);
    bulk.reset();
    const rows = shownRows();
    count.set(rows.length, contactsCache().length, filter);
    if (!rows.length) { tbodyWrap.appendChild(emptyState('☺', 'Няма контакти в тази група.', '+ Нов контакт', () => contactForm())); return; }
    const tb = el('tbody');
    rows.forEach((c) => tb.appendChild(clickableRow(() => contactDetail(c), 'Отвори ' + c.name,
      bulk.cell(c.id, c.name),
      // Името е на един ред (дългото с „…"), за да са редовете ниски като в
      // „Доставки"; МОЛ-ът е в подсказката при задържане, не на втори ред.
      el('td', { class: 'reg-cut', style: 'font-weight:600', title: c.name + (c.mol ? ' — МОЛ: ' + c.mol : '') },
        el('span', { text: c.name }),
        isSupplier(c) ? el('span', { style: 'margin-left:7px;font-size:11px;font-weight:600;color:#7c3aed;background:#f3e8ff;border-radius:6px;padding:1px 7px', text: 'Доставчик' }) : null),
      el('td', { class: 'hide-sm', style: 'font-weight:600' }, codesText(c)),
      el('td', { class: 'hide-sm' }, c.eik || '—'),
      cutCell(c.city, { class: 'reg-cut hide-sm' }),
      el('td', {}, c.phone || '—'),
      cutCell(c.email, { class: 'reg-cut hide-sm' }),
      el('td', { class: 'hide-sm' }, verifyBadges(c))
    )));
    tbodyWrap.appendChild(el('table', { class: REG_TABLE }, el('thead', {}, el('tr', {},
      bulk.headTh,
      el('th', { scope: 'col', text: 'Име' }), el('th', { scope: 'col', class: 'hide-sm', text: 'Шифър' }), el('th', { scope: 'col', class: 'hide-sm', text: 'ЕИК / Булстат' }), el('th', { scope: 'col', class: 'hide-sm', text: 'Град' }), el('th', { scope: 'col', text: 'Телефон' }), el('th', { scope: 'col', class: 'hide-sm', text: 'Имейл' }), el('th', { scope: 'col', class: 'hide-sm', title: 'Зелено = ДДС номерът е проверен във VIES; синьо = данните са от Търговския регистър', text: 'Проверка' })
    )), tb));
  }
  // Видът е като в „Доставки" (Макс, 29.10.2026): заглавието вляво, всички
  // бутони вдясно на един ред, основният „+ Нов контакт" — последен.
  const exportBtn = el('button', { class: 'btn sm ghost', type: 'button', text: 'Експорт', onClick: () => {
    const names = { all: ['Kontakti', 'kontakti'], clients: ['Klienti', 'klienti'], suppliers: ['Dostavchitsi', 'dostavchitsi'] };
    exportContactsXlsx(shownRows(), names[typeFilter] || names.all);
  } });
  const importBtn = el('button', { class: 'btn sm ghost', type: 'button', text: 'Импорт', onClick: () => importContactsXlsx() });
  panel.appendChild(regHead('Контакти', [exportBtn, importBtn],
    el('button', { class: 'btn sm', type: 'button', text: '+ Нов контакт', onClick: () => contactForm() }),
    bulk.toolbar));
  const search = searchInput({ placeholder: 'Име, ЕИК, град, телефон, имейл…', ariaLabel: 'Търсене в „Контакти"', onSearch: (q) => { filter = q; draw(); } });
  // Отстъпът на .seg е за самостоятелен ред — в лентата с филтри би избутал
  // бутоните встрани от надписа „Вид".
  const tabsWrap = el('div', { class: 'seg contacts-seg', role: 'group', 'aria-label': 'Вид контакт', style: 'margin:0' });
  // aria-pressed: кой филтър е включен личеше само по цвят на бутона.
  const mkTab = (key, label) => { const b = el('button', { type: 'button', 'aria-pressed': typeFilter === key ? 'true' : 'false', text: label, onClick: () => { typeFilter = key; state()._contactsTab = key; [...tabsWrap.children].forEach((x) => { x.setAttribute('aria-pressed', 'false'); }); b.setAttribute('aria-pressed', 'true'); draw(); } }); return b; };
  tabsWrap.appendChild(mkTab('all', 'Всички'));
  tabsWrap.appendChild(mkTab('clients', 'Клиенти'));
  tabsWrap.appendChild(mkTab('suppliers', 'Доставчици'));
  const typeField = regField('Вид', tabsWrap);
  // label for= бива само към поле за писане; групата бутони има свое име
  // (aria-label), затова тук надписът е само видим текст.
  const typeLbl = typeField.querySelector('label');
  if (typeLbl) typeLbl.removeAttribute('for');
  panel.appendChild(regFilters(regField('Търсене', search, 'l'), typeField));
  panel.appendChild(count);
  panel.appendChild(tbodyWrap);
  page.appendChild(panel);
  try { setContacts(await api('/contacts' + cq())); draw(); } catch (e) { toast(e.message, true); }
}

function contactForm(existing) {
  const c = existing || {};
  const formOpenedAt = Date.now();
  let viesOk = !!(c.vies_verified);
  const f = {
    name: input({ value: c.name || '', placeholder: 'Фирма ООД или име' }),
    type: el('select', {}, ...[['client', 'Клиент'], ['lead', 'Потенциален'], ['partner', 'Партньор'], ['supplier', 'Доставчик']].map(([v, l]) => el('option', { value: v, text: l, selected: (c.type || 'client') === v }))),
    eik: input({ value: c.eik || '', placeholder: '123456789' }),
    vat_number: input({ value: c.vat_number || '', placeholder: 'BG123456789' }),
    mol: input({ value: c.mol || '', placeholder: 'Управител' }),
    city: input({ value: c.city || '', placeholder: 'София' }),
    address: input({ value: c.address || '', placeholder: 'ул. …' }),
    phone: input({ value: c.phone || '' }),
    email: input({ type: 'email', value: c.email || '' }),
    website: input({ value: c.website || '' }),
    acc_code: input({ value: c.acc_code || '' }),
    // В Конто доставчиците са в друга номенклатура (80) — кодът там е друг.
    supplier_code: input({ value: c.supplier_code || '', placeholder: 'ако е и доставчик' }),
    // Държавата решава ВОД/OSS при фактуриране; заварен контакт без нея е
    // български (сървърът прави същото). Непознат заварен код остава като
    // опция, за да не се подмени тихо при запис.
    country: (() => {
      const cur = String(c.country || 'BG').toUpperCase();
      const list = COUNTRIES.some((k) => k.code === cur) ? COUNTRIES : COUNTRIES.concat([{ code: cur, name: cur }]);
      return el('select', {}, ...list.map((k) => el('option', { value: k.code, text: k.name, selected: k.code === cur })));
    })(),
    is_person: el('input', { type: 'checkbox', checked: !!c.is_person }),
  };
  // Ценовата листа (т. 5 a): с нея цената на артикула във фактурата идва
  // сама. Полето се вижда само ако фирмата има листи (или клиентът вече е на
  // листа); незаредени листи не се пращат — сървърът пази старата.
  f.price_list_id = el('select', {}, el('option', { value: '', text: '— основна цена —' }));
  const plField = field('Ценова листа', f.price_list_id);
  plField.style.display = 'none';
  api('/price-lists' + cq()).then((lists) => {
    (Array.isArray(lists) ? lists : []).forEach((l) => f.price_list_id.appendChild(el('option', { value: l.id, text: l.name + (l.active === false ? ' (неактивна)' : ''), selected: Number(c.price_list_id) === Number(l.id) })));
    f.price_list_id.dataset.loaded = '1';
    if ((Array.isArray(lists) && lists.length) || c.price_list_id) plField.style.display = '';
  }).catch(() => {});
  const _accLen = Number((activeCompanyObj() || {}).acc_code_length) || 0;
  if (_accLen) f.acc_code.placeholder = _accLen + ' цифри';
  const accNextBtn = el('button', { class: 'btn sm ghost', type: 'button', text: 'Следващ свободен №', onClick: () => {
    let mx = 0;
    (contactsCache() || []).forEach((x) => { if (x.company_id !== state().activeCompany) return; const d = parseInt(String(x.acc_code || '').replace(/\D/g, ''), 10); if (d) mx = Math.max(mx, d); });
    const n = String(mx + 1);
    f.acc_code.value = _accLen ? n.padStart(_accLen, '0') : n;
    // Потвърждение какво стана — иначе числото се появява тихо встрани (В.4).
    toast('Предложен е шифър ' + f.acc_code.value + '.');
  } });
  const accCodeField = el('div', { style: 'display:flex;gap:6px;align-items:center' }, f.acc_code, _accLen ? el('span', { style: 'font-size:12px;color:var(--muted);white-space:nowrap', text: '(' + _accLen + ' цифри)' }) : null, accNextBtn);
  const body = el('div', {},
    field('Име / Фирма', f.name),
    row(field('Вид', f.type), field('ЕИК / Булстат', f.eik)),
    row(field('ДДС номер', f.vat_number), field('МОЛ (представляващ)', f.mol)),
    el('div', { style: 'margin:-2px 0 10px' }, viesButton(() => f.vat_number.value || f.eik.value, (r) => {
      viesOk = true;
      if (!f.name.value && r.name) f.name.value = r.name;
      if (!f.address.value && r.address) f.address.value = r.address;
      if (!f.vat_number.value) f.vat_number.value = r.countryCode + r.vatNumber;
      // Държавата се попълва само ако човекът не я е сменил сам (още е BG):
      // ръчният избор е по-силен от регистъра.
      const iso = VIES_TO_ISO[r.countryCode] || r.countryCode;
      if ((!f.country.value || f.country.value === 'BG') && iso && COUNTRIES.some((k) => k.code === iso)) f.country.value = iso;
    })),
    row(field('Държава', f.country), el('div', { class: 'field' }, el('label', { class: 'choice' }, f.is_person, el('span', { text: 'Физическо лице' })))),
    row(field('Град', f.city), field('Адрес', f.address)),
    row(field('Телефон', f.phone), field('Имейл', f.email)),
    row(field('Счетоводен шифър', accCodeField), field('Шифър като доставчик', f.supplier_code)),
    field('Уебсайт', f.website),
    plField
  );
  const save = async () => {
    const payload = { company_id: state().activeCompany };
    // Отметката носи смисъла си в .checked — .value на checkbox е винаги „on".
    for (const k in f) payload[k] = f[k].type === 'checkbox' ? f[k].checked : (('iso' in f[k].dataset) ? f[k].dataset.iso : f[k].value);
    if (f.price_list_id.dataset.loaded !== '1') delete payload.price_list_id;
    if (_accLen && payload.acc_code) { const m = String(payload.acc_code).trim().match(/^(.*?)(\d+)$/); if (m) { const pre = m[1]; let dig = m[2]; const need = _accLen - pre.length; if (need > dig.length) dig = dig.padStart(need, '0'); payload.acc_code = pre + dig; } }
    const today = localTodayIso();
    payload.vies_verified = viesOk ? (c.vies_verified || today) : '';
    payload.data_verified = (window.__registryHit && window.__registryHit >= formOpenedAt) ? today : (c.data_verified || '');
    try {
      if (existing) await api('/contacts/' + existing.id, { method: 'PUT', body: JSON.stringify(payload) });
      else await api('/contacts', { method: 'POST', body: JSON.stringify(payload) });
      closeModal(); toast('Записано.'); window.CRM.renderView($('#page'));
    } catch (e) { toast(e.message, true); }
  };
  const foot = el('div', {}, el('button', { class: 'btn ghost', text: 'Отказ', onClick: closeModal }), el('button', { class: 'btn', text: 'Запази', onClick: save, style: 'margin-left:8px' }));
  openModal({ title: existing ? 'Редакция на контакт' : 'Нов контакт', body, foot, wide: true });
}

async function contactDetail(c) {
  let notes = [];
  try { notes = await api('/notes' + cq() + '&contact_id=' + c.id); } catch (e) {}
  const body = el('div', {});
  body.appendChild(el('div', { class: 'detail-meta' },
    metaItem('ЕИК / Булстат', c.eik || '—'), metaItem('ДДС номер', c.vat_number || '—'),
    metaItem('МОЛ', c.mol || '—'), metaItem('Град', c.city || '—'),
    metaItem('Телефон', c.phone || '—'), metaItem('Имейл', c.email || '—'),
    metaItem('Адрес', c.address || '—'), metaItem('Вид', ({ client: 'Клиент', lead: 'Потенциален', partner: 'Партньор', supplier: 'Доставчик' })[c.type] || c.type),
    // Показват се само когато носят информация — България и фирма са нормата.
    (c.acc_code || c.supplier_code) ? metaItem('Шифър', codesText(c)) : null,
    (c.country && c.country !== 'BG') ? metaItem('Държава', countryName(c.country)) : null,
    c.is_person ? metaItem('Физическо лице', 'да') : null
  ));
  body.appendChild(el('div', { style: 'display:flex;gap:8px;margin-bottom:18px' },
    el('button', { class: 'btn sm', text: 'Редактирай', onClick: () => { closeModal(); contactForm(c); } }),
    el('button', { class: 'btn sm ghost', text: 'Създай фактура', onClick: () => { closeModal(); window.invoiceForm({ contact_id: c.id }); } }),
    isAdminUser() && el('button', { class: 'btn sm danger', text: 'Изтрий', onClick: async () => { if (await confirmDialog('Да изтрия ли този контакт?', { okText: 'Изтрий', danger: true })) { try { await api('/contacts/' + c.id, { method: 'DELETE' }); toast('Изтрито.'); window.CRM.renderView($('#page')); } catch (e) { toast('Изтриването не се получи. ' + e.message, true); } } } })
  ));
  if (c.type === 'supplier') {
  let vops = [];
  try { vops = await api('/protocols' + cq()); } catch (e) {}
  const cn = (c.name || '').trim().toLowerCase();
  const cvat = ((c.vat_number || c.eik || '') + '').replace(/\s/g, '').toLowerCase();
  const mine = (vops || []).filter((p) => { const n = (p.supplier_name || '').trim().toLowerCase(); const vv = (p.supplier_vat || '').replace(/\s/g, '').toLowerCase(); return (n && cn && n === cn) || (vv && cvat && vv === cvat); });
  body.appendChild(el('h4', { text: 'ВОП протоколи (доставки от ЕС)', style: 'margin:6px 0 12px' }));
  if (!mine.length) body.appendChild(el('p', { class: 'empty', style: 'padding:14px', text: 'Няма свързани ВОП протоколи.' }));
  else { body.appendChild(el('table', {}, el('thead', {}, el('tr', {}, el('th', { scope: 'col', text: '№' }), el('th', { scope: 'col', text: 'Дата' }), el('th', { scope: 'col', text: 'Фактура' }), el('th', { scope: 'col', class: 'num', text: 'Основа' }), el('th', { scope: 'col', class: 'num', text: 'ДДС' }))), el('tbody', {}, ...mine.sort((x, y) => (y.protocol_date || '').localeCompare(x.protocol_date || '')).map((p) => el('tr', {}, el('td', { text: '№ ' + (p.number || '—') }), el('td', { text: dfmt(p.protocol_date) }), el('td', { text: p.supplier_invoice_number || '—' }), el('td', { class: 'num' }, money(p.tax_base, p.currency)), el('td', { class: 'num' }, money(p.vat_amount, p.currency)))))));
  }
}
  body.appendChild(notesHistory(c, notes));
  if (isAdminUser()) body.appendChild(gdprBlock(c));
  openModal({ title: c.name, body, wide: true });
}

// GDPR (грешка от картата, 16.10.2026: износът и анонимизацията бяха само в
// сървъра, без бутон). Само за администратор — както адресите
// /api/gdpr/contact/:id/… Анонимизацията е НЕОБРАТИМА: питаме с името.
function gdprBlock(c) {
  const exportBtn = el('button', { class: 'btn sm ghost', type: 'button', text: 'Изнеси данните (JSON)', onClick: () => { window.open('/api/gdpr/contact/' + c.id + '/export', '_blank'); } });
  const anonBtn = el('button', { class: 'btn sm danger', type: 'button', text: 'Анонимизирай', onClick: async () => {
    if (!(await confirmDialog('Анонимизиране на „' + (c.name || '') + '": личните данни на контакта се заличават, сделките, задачите и бележките му се изтриват. Фактурите, протоколите и доставките остават (законово задължение). Действието е НЕОБРАТИМО.', { okText: 'Анонимизирай', danger: true }))) return;
    try {
      await api('/gdpr/contact/' + c.id + '/anonymize', { method: 'POST', body: JSON.stringify({ confirm: true }) });
      closeModal();
      toast('Контактът е анонимизиран.');
      window.CRM.renderView($('#page'));
    } catch (e) { toast(e.message, true); }
  } });
  return el('details', { class: 'form-sec gdpr-sec', style: 'margin-top:18px' },
    el('summary', { text: 'Лични данни (GDPR)' }),
    el('div', { class: 'form-sec-body' },
      el('p', { class: 'cal-hint', style: 'padding:0 0 8px', text: 'Право на достъп/преносимост — изнася всичко за контакта в един файл. Право „да бъдеш забравен" — заличава личните данни, документите по ЗСч остават.' }),
      el('div', { style: 'display:flex;gap:8px;flex-wrap:wrap' }, exportBtn, anonBtn)));
}
const NOTE_TYPES = [['note', 'Бележка'], ['call', 'Обаждане'], ['meeting', 'Среща'], ['email', 'Имейл']];
const noteTypeLabel = (t) => (NOTE_TYPES.find((x) => x[0] === t) || [t, t])[1];

// „История на разговорите" с „+ Бележка" (ПРОМТ-19, т. 6). Историята беше
// само за четене — бележка към клиента се пишеше в раздел „Бележки", където
// после трябваше да се търси. Сега се записва тук, към този контакт и
// неговата фирма, и списъкът се опреснява на място.
function notesHistory(c, initial) {
  let notes = initial || [];
  const wrap = el('div', { class: 'notes-history' });
  const list = el('div', {});
  const companyId = c.company_id || state().activeCompany;

  function drawList() {
    clear(list);
    if (!notes.length) { list.appendChild(el('p', { class: 'empty', style: 'padding:18px', text: 'Още няма записани бележки.' })); return; }
    const tl = el('div', { class: 'timeline' });
    notes.slice().sort((a, b) => (b.date || '').localeCompare(a.date || '') || (Number(b.id) || 0) - (Number(a.id) || 0)).forEach((n) => {
      tl.appendChild(el('div', { class: 'item' },
        el('div', { class: 'when', text: dfmt(n.date) + ' · ' + noteTypeLabel(n.type) }),
        el('div', { text: n.body })
      ));
    });
    list.appendChild(tl);
  }

  const form = el('div', { class: 'note-form', hidden: true });
  const addBtn = el('button', { class: 'btn sm', type: 'button', text: '+ Бележка', 'aria-expanded': 'false', onClick: () => openForm() });

  function closeForm() {
    form.hidden = true;
    addBtn.setAttribute('aria-expanded', 'false');
    clear(form);
    addBtn.focus();
  }

  function openForm() {
    clear(form);
    const date = dateInput(localTodayIso());
    const type = el('select', {}, ...NOTE_TYPES.map(([v, t]) => el('option', { value: v, text: t })));
    const text = el('textarea', { rows: '3', maxlength: '8000' });
    let busy = false;
    const save = el('button', { class: 'btn sm', type: 'button', text: 'Запази', onClick: async () => {
      if (busy) return;
      if (!dateInputsValid(form)) { toast('Не разбрах датата — поправете червеното поле.', true); return; }
      if (!date.dataset.iso) { toast('Въведете дата.', true); date.focus(); return; }
      if (!text.value.trim()) { toast('Въведете текст на бележката.', true); text.focus(); return; }
      busy = true;
      try {
        await api('/notes', { method: 'POST', body: JSON.stringify({
          company_id: companyId, contact_id: c.id, type: type.value, body: text.value.trim(), date: date.dataset.iso,
        }) });
        try { notes = await api('/notes?company_id=' + companyId + '&contact_id=' + c.id); } catch (e) {}
        drawList();
        toast('Бележката е записана.');
        closeForm();
      } catch (e) {
        toast('Бележката не се записа. ' + e.message, true);
      } finally { busy = false; }
    } });
    form.append(
      row(field('Дата', date, { size: 's' }), field('Вид', type, { size: 's' })),
      field('Текст', text),
      el('div', { style: 'display:flex;gap:8px;margin-bottom:14px' }, save, el('button', { class: 'btn sm ghost', type: 'button', text: 'Отказ', onClick: closeForm })));
    form.hidden = false;
    addBtn.setAttribute('aria-expanded', 'true');
    text.focus();
  }

  wrap.append(
    el('div', { style: 'display:flex;align-items:center;gap:12px;margin:6px 0 12px' },
      el('h4', { text: 'История на разговорите', style: 'margin:0' }), addBtn),
    form, list);
  drawList();
  return wrap;
}

function metaItem(k, v) { return el('div', {}, el('div', { class: 'k', text: k }), el('div', { class: 'v', text: v })); }


function exportContactsXlsx(rows, names) {
  rows = rows || contactsCache() || [];
  names = names || ['Kontakti', 'kontakti'];
  if (!rows.length) { toast('Няма записи за експорт при този изглед.', true); return; }
  const data = rows.map((c) => ({
    'Име': c.name || '', 'Вид': c.type || '', 'ЕИК': c.eik || '', 'ДДС номер': c.vat_number || '',
    'МОЛ': c.mol || '', 'Град': c.city || '', 'Адрес': c.address || '', 'Телефон': c.phone || '',
    'Имейл': c.email || '', 'Уебсайт': c.website || '', 'Счетоводен шифър': c.acc_code || '', 'Шифър като доставчик': c.supplier_code || '',
  }));
  xlsxDownload(data, names[0], names[1] + '.xlsx');
}

// (Б.7) Вносът е ДВУСТЪПКОВ: файлът само се прочита, после преглед и
// изричен „Внеси" (логиката е в ./import.js). Досега редовете влизаха
// необратимо още с избора на файл и същият файл втори път удвояваше всичко.
function importContactsXlsx() {
  if (!state().activeCompany) { toast('Първо изберете фирма.', true); return; }
  impReadFile((rows, file) => contactsImportPreview(rows, file));
}


